import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import ConversationList from "./ConversationList";
import MessageThread from "./MessageThread";
import EmptyChat from "./EmptyChat";

import {
  chatGetContacts,
  chatGetConversations,
  chatReceiveMessage,
  chatTypingStart,
  chatTypingStop,
  chatUserOnline,
  chatUserOffline,
  chatReactionAdded,
  chatReactionRemoved,
  chatMessageEdited,
  chatMessageDeleted,
  chatMessageRead,
} from "store/actions/Chat";
import { connectSocket, disconnectSocket } from "services/chat/socketService";

import "./Chat.scss";

function Chat() {
  const dispatch = useDispatch();
  const { activeConversation, contacts, conversations } = useSelector((state) => state.Chat);
  const [, setSocketConnected] = useState(false);
  const activeConversationRef = useRef(activeConversation);
  const contactsRef = useRef(contacts);
  const conversationsRef = useRef(conversations);

  const loggedInUser = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
  const currentUserId = loggedInUser.userId;

  // Clear pending (unsent) conversation when navigating away from chat
  useEffect(() => {
    return () => {
      if (activeConversation?.pending) {
        dispatch({ type: "CHAT_SET_ACTIVE_CONVERSATION", payload: null });
      }
    };
  }, [activeConversation, dispatch]);

  // Keep refs in sync so the socket callback always has latest values
  useEffect(() => {
    activeConversationRef.current = activeConversation;
  }, [activeConversation]);

  useEffect(() => {
    contactsRef.current = contacts;
  }, [contacts]);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  const showNotification = useCallback(
    (message) => {
      // Don't notify for own messages
      if (message.senderId === currentUserId) return;

      // Don't notify if tab is focused and viewing this conversation
      if (document.hasFocus() && activeConversationRef.current?.conversationId === message.conversationId) return;

      if ("Notification" in window && Notification.permission === "granted") {
        // Find sender name from contacts or conversations
        let senderName = "New message";
        const contact = contactsRef.current.find((c) => c.userId === message.senderId);
        if (contact) {
          senderName = `${contact.firstName} ${contact.lastName}`;
        } else {
          // Try to find from conversation participants
          const matchedConv = conversationsRef.current.find((conv) =>
            (conv.Participants || []).some((p) => p.userId === message.senderId && p.userInfo)
          );
          if (matchedConv) {
            const participant = (matchedConv.Participants || []).find((p) => p.userId === message.senderId);
            senderName = `${participant.userInfo.firstName} ${participant.userInfo.lastName}`;
          }
        }

        let body = message.content || "New message";
        if (message.messageType === "image") body = "Sent an image";
        else if (message.messageType === "file") body = "Sent a file";

        const notification = new Notification(senderName, {
          body,
          icon: "/favicon.ico",
          tag: `chat-${message.conversationId}`,
          renotify: true,
        });

        // Focus the window when notification is clicked
        notification.onclick = () => {
          window.focus();
          notification.close();
        };

        // Auto-close after 5 seconds
        setTimeout(() => notification.close(), 5000);
      }
    },
    [currentUserId]
  );

  useEffect(() => {
    dispatch(chatGetContacts());
    dispatch(chatGetConversations());

    // Connect socket
    const token = localStorage.getItem("accessToken");
    if (token) {
      const socket = connectSocket(token);

      socket.on("connect", () => setSocketConnected(true));
      socket.on("disconnect", () => setSocketConnected(false));

      // Listen for real-time events
      socket.on("message:new", (message) => {
        dispatch(chatReceiveMessage({ ...message, currentUserId }));
        showNotification(message);
      });

      socket.on("typing:start", (data) => {
        dispatch(chatTypingStart(data));
      });

      socket.on("typing:stop", (data) => {
        dispatch(chatTypingStop(data));
      });

      socket.on("user:online", (data) => {
        dispatch(chatUserOnline(data));
      });

      socket.on("user:offline", (data) => {
        dispatch(chatUserOffline(data));
      });

      socket.on("reaction:added", (data) => {
        dispatch(chatReactionAdded(data));
      });

      socket.on("reaction:removed", (data) => {
        dispatch(chatReactionRemoved(data));
      });

      socket.on("message:edited", (data) => {
        dispatch(chatMessageEdited(data));
      });

      socket.on("message:deleted", (data) => {
        dispatch(chatMessageDeleted(data));
      });

      socket.on("message:read", (data) => {
        dispatch(chatMessageRead({ ...data, currentUserId }));
      });
    }

    return () => {
      disconnectSocket();
    };
  }, []);

  return (
    <div className="chat-container">
      <ConversationList />
      {activeConversation ? <MessageThread /> : <EmptyChat />}
    </div>
  );
}

export default Chat;
